import React from 'react';
import { siError, useModuleRankings } from '../../hooks/useStudentRankings';
import { CARD, KindBadge, percent, score } from './rankingText';

function People({ title, rows, tone, onOpenLearner }) {
  return (
    <div className="min-w-0 space-y-1">
      <p
        className={`text-[11px] font-semibold uppercase tracking-wide ${tone}`}
      >
        {title}
      </p>
      {rows?.length ? (
        <ol className="m-0 list-none space-y-0.5 p-0">
          {rows.map((row) => (
            <li
              key={row.student_id}
              className="flex items-baseline justify-between gap-2 text-sm"
            >
              <button
                type="button"
                onClick={() => onOpenLearner(row.student_id)}
                className="truncate text-left text-gray-800 hover:text-persian-600 hover:underline"
                title={row.name}
              >
                <span className="mr-1 text-xs tabular-nums text-gray-400">
                  {row.rank}.
                </span>
                {row.name || row.email || row.student_id}
              </button>
              <span className="shrink-0 tabular-nums text-xs text-gray-600">
                {score(row.score)}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-xs text-gray-400">Nobody has started yet.</p>
      )}
    </div>
  );
}

export default function ModuleRankings({ cohortId, onOpenLearner }) {
  const { data, isLoading, error } = useModuleRankings(cohortId);
  if (isLoading)
    return (
      <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
    );
  if (error) {
    return (
      <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
        {siError(error, "We couldn't load the module rankings.")}
      </div>
    );
  }
  const modules = data?.modules ?? [];
  if (!modules.length) {
    return (
      <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
        This cohort has no modules to rank yet.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {modules.map((module) => (
        <section key={module.module_id} className={`${CARD} space-y-4 p-5`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p
                className="truncate text-sm font-semibold text-gray-900"
                title={module.name}
              >
                {module.name}
              </p>
              <p className="text-xs text-gray-500">
                {(module.started ?? 0).toLocaleString()} of{' '}
                {(module.learners ?? 0).toLocaleString()} started · average
                content {percent(module.average_content)}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <KindBadge kind={module.kind} />
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {score(module.average_score)}
              </p>
              <p className="text-[10px] text-gray-400">average score</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <People
              title="Top 5"
              rows={module.top}
              tone="text-green-700"
              onOpenLearner={onOpenLearner}
            />
            <People
              title="Struggling"
              rows={module.struggling}
              tone="text-red-700"
              onOpenLearner={onOpenLearner}
            />
          </div>
        </section>
      ))}
    </div>
  );
}
